import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  clearCanvasAiConversation,
  readCanvasAiConversation,
  storeCanvasAiConversation,
} from "./canvasAi.storage";
import { getCanvasAiErrorMessage } from "./canvasAiErrors";
import { runCanvasAiTurn } from "./runCanvasAiTurn";
import { useOpenRouterAccess } from "./useOpenRouterAccess";

const CONFLICT_MESSAGE =
  "The canvas changed while AI was working. Nothing was applied; please retry.";

class CanvasAiConflictError extends Error {
  constructor() {
    super(CONFLICT_MESSAGE);
    this.name = "CanvasAiConflictError";
  }
}

const createMessageId = () =>
  window.crypto?.randomUUID?.() ?? `canvas-ai-${Date.now()}-${Math.random()}`;

const readStoredMessages = (documentKey) =>
  readCanvasAiConversation(documentKey).map((message) => ({
    ...message,
    id: createMessageId(),
  }));

const createTurn = (prompt) => ({
  assistant: {
    content: "",
    id: createMessageId(),
    isTransient: true,
    role: "assistant",
  },
  user: {
    content: prompt,
    id: createMessageId(),
    isTransient: true,
    role: "user",
  },
});

const updateMessage = ({ messages, messageId, updates }) =>
  messages.map((message) =>
    message.id === messageId ? { ...message, ...updates } : message
  );

const toModelMessages = ({ messages, prompt }) => [
  ...messages
    .filter(
      (message) =>
        !message.isTransient && ["assistant", "user"].includes(message.role)
    )
    .map(({ content, role }) => ({ content, role })),
  { content: prompt, role: "user" },
];

const commitDraft = ({ checkpoint, engine, result }) => {
  if (!result.hasPatch) return null;
  const commit = engine.commitSceneTransaction({
    baseRevision: checkpoint.revision,
    elements: result.elements,
    selectedIds: result.changedIds,
  });
  if (!commit.committed) throw new CanvasAiConflictError();
  return commit.revision;
};

const buildAssistantContent = (result) => {
  if (result.text) return result.text;
  if (result.hasPatch) return `Updated the canvas: ${result.summary}`;
  return "I could not produce a response. Please try again.";
};

const performTurn = async ({
  abortSignal,
  apiKey,
  engine,
  messages,
  modelId,
  onText,
  prompt,
}) => {
  const checkpoint = engine.getSceneCheckpoint();
  const result = await runCanvasAiTurn({
    abortSignal,
    apiKey,
    checkpoint,
    messages: toModelMessages({ messages, prompt }),
    modelId,
    onText,
  });
  return {
    content: buildAssistantContent(result),
    revision: commitDraft({ checkpoint, engine, result }),
  };
};

const finalizeTurnMessages = ({ messages, result, turn }) =>
  messages.map((message) => {
    if (message.id === turn.user.id) return { ...message, isTransient: false };
    if (message.id !== turn.assistant.id) return message;
    return { ...message, content: result.content, isTransient: false };
  });

/**
 * Owns credential/model state and one document-scoped canvas AI conversation.
 * @param {{ documentKey: string, engine: object }} params
 * @returns {object}
 */
export const useCanvasAiAssistant = ({ documentKey, engine }) => {
  const access = useOpenRouterAccess();
  const [error, setError] = useState("");
  const [lastCommitRevision, setLastCommitRevision] = useState(null);
  const [messages, setMessages] = useState(() =>
    readStoredMessages(documentKey)
  );
  const [status, setStatus] = useState("idle");
  const requestControllerRef = useRef(null);
  const requestIdRef = useRef(null);
  const elementRevision = useSyncExternalStore(
    engine.subscribeScene,
    engine.getElementRevision
  );

  useEffect(() => {
    requestIdRef.current = null;
    requestControllerRef.current?.abort();
    setMessages(readStoredMessages(documentKey));
    setError("");
    setLastCommitRevision(null);
    setStatus("idle");
  }, [documentKey]);

  useEffect(
    () => () => {
      requestIdRef.current = null;
      requestControllerRef.current?.abort();
    },
    []
  );

  const removeCredential = useCallback(() => {
    requestIdRef.current = null;
    requestControllerRef.current?.abort();
    access.removeCredential();
    setError("");
    setStatus("idle");
  }, [access]);

  const selectModel = useCallback((nextModelId) => {
    if (access.modelId && access.modelId !== nextModelId) {
      setMessages((current) => [
        ...current,
        {
          content: `Model changed to ${nextModelId}`,
          id: createMessageId(),
          isTransient: true,
          role: "notice",
        },
      ]);
    }
    access.selectModel(nextModelId);
  }, [access]);

  const sendMessage = useCallback(
    async (promptValue) => {
      const prompt = String(promptValue).trim();
      if (!prompt || !access.apiKey || !access.modelId || status === "streaming") return;
      const turn = createTurn(prompt);
      const baseMessages = messages;
      const pendingMessages = [...baseMessages, turn.user, turn.assistant];
      const controller = new AbortController();
      const requestId = createMessageId();
      requestControllerRef.current = controller;
      requestIdRef.current = requestId;
      setMessages(pendingMessages);
      setError("");
      setStatus("streaming");
      try {
        const result = await performTurn({
          abortSignal: controller.signal,
          apiKey: access.apiKey,
          engine,
          messages: baseMessages,
          modelId: access.modelId,
          onText: (content) =>
            setMessages((current) =>
              updateMessage({
                messageId: turn.assistant.id,
                messages: current,
                updates: { content },
              })
            ),
          prompt,
        });
        if (requestIdRef.current !== requestId) return;
        const completedMessages = finalizeTurnMessages({
          messages: pendingMessages,
          result,
          turn,
        });
        setMessages(completedMessages);
        storeCanvasAiConversation({ documentKey, messages: completedMessages });
        setLastCommitRevision(result.revision);
      } catch (turnError) {
        if (requestIdRef.current !== requestId) return;
        const errorMessage = getCanvasAiErrorMessage(turnError);
        setError(errorMessage);
        setMessages((current) =>
          updateMessage({
            messageId: turn.assistant.id,
            messages: current,
            updates: { content: errorMessage, isError: true },
          })
        );
      } finally {
        if (requestIdRef.current === requestId) {
          requestIdRef.current = null;
          requestControllerRef.current = null;
          setStatus("idle");
        }
      }
    }, [access.apiKey, access.modelId, documentKey, engine, messages, status]
  );

  const startNewChat = useCallback(() => {
    requestIdRef.current = null;
    requestControllerRef.current?.abort();
    clearCanvasAiConversation(documentKey);
    setMessages([]);
    setError("");
    setLastCommitRevision(null);
    setStatus("idle");
  }, [documentKey]);

  const undoLastChange = useCallback(() => {
    if (!lastCommitRevision || !engine.undoSceneTransaction(lastCommitRevision)) {
      setError("The canvas changed after that AI update, so it cannot be undone here.");
      return;
    }
    setLastCommitRevision(null);
    setMessages((current) => [
      ...current,
      {
        content: "AI canvas update undone",
        id: createMessageId(),
        isTransient: true,
        role: "notice",
      },
    ]);
  }, [engine, lastCommitRevision]);

  return {
    canUndoLastChange:
      Boolean(lastCommitRevision) && lastCommitRevision === elementRevision,
    clearError: () => {
      setError("");
      access.clearAccessError();
    },
    connectCredential: access.connectCredential,
    error: error || access.error,
    hasCredential: access.hasCredential,
    isCredentialBusy: access.isCredentialBusy,
    isModelsLoading: access.isModelsLoading,
    isRemembered: access.isRemembered,
    messages,
    modelId: access.modelId,
    models: access.models,
    removeCredential,
    selectModel,
    sendMessage,
    startNewChat,
    status,
    stopGeneration: () => requestControllerRef.current?.abort(),
    undoLastChange,
  };
};
